import type { CaptureErrorKind } from './uniCamUtils'

/**
 * Shutter-capture outcome counters for the current app run.
 *
 * Why this exists: `flash_capture_retry` changes retry timing on the paid capture path,
 * but nothing today reports whether a booth's captures actually get better. Operators
 * report "sometimes it doesn't take the photo", which is unfalsifiable without numbers.
 * These counters ride the existing app-settings telemetry so a flag cohort can be
 * compared against an un-flagged one on real bookings.
 *
 * Scope is deliberately narrow: PHYSICAL SHUTTER captures only (the liveview-grab path
 * has entirely different failure modes and would dilute the signal). Counts are
 * cumulative since app start and never reset — telemetry is sent at View mount and from
 * the settings modal, so the backend sees a monotonically growing snapshot per run and
 * can difference consecutive reports if it wants per-interval rates.
 *
 * Module-level rather than a store: nothing renders from these, and keeping them out of
 * zustand avoids re-rendering the booth on every shutter press.
 */

/**
 * Upper bounds (ms) of the shutter-duration histograms, ascending; a final overflow
 * bucket catches everything above the last bound.
 *
 * Chosen around the two numbers that actually govern this path. libgphoto2 blocks on
 * autofocus INSIDE the capture call — it half-presses with AF, then polls the camera
 * for a focus result before it will full-press — and that poll has a hard ceiling:
 * 2000ms on the WASM backend (libgphoto2 2.5.28, the version compiled into our
 * libapi.wasm) and 500ms on the native daemon (2.5.34; upstream lowered it in 2.5.32).
 * The buckets straddle both, so anything that moves autofocus off the critical path
 * shows up as mass migrating left across the 500 and 2000 boundaries rather than as a
 * mean that drifts a little.
 */
export const SHUTTER_DURATION_BUCKET_BOUNDS_MS = [
  250, 500, 750, 1000, 1500, 2000, 3000, 5000
] as const

export interface DurationSnapshot {
  /** Samples recorded. */
  count: number
  /** Sum of all samples — with `count`, gives the mean without keeping samples. */
  totalMs: number
  /** Slowest single sample seen this run. */
  maxMs: number
  /** `totalMs / count`, rounded; 0 when there are no samples. */
  meanMs: number
  /**
   * Upper bound of the bucket containing the p50/p95 sample, or `null` when that
   * percentile lands in the overflow bucket (i.e. above the last bound). Histogram
   * percentiles, so read them as "at or under this many ms", not as exact values.
   */
  p50Ms: number | null
  p95Ms: number | null
  /** Counts per bucket; length is bounds.length + 1, the last being the overflow. */
  buckets: number[]
  /** Echoed so the backend never has to hardcode the bucket layout. */
  bucketBoundsMs: readonly number[]
}

export interface CaptureStatsSnapshot {
  /** Shutter captures attempted (one per capture request, not per retry). */
  attempts: number
  /** Shutter captures that produced a file, including those that needed retries. */
  successes: number
  /** Shutter captures that exhausted their retry schedule and threw. */
  failures: number
  /** Successes that took at least one retry — the number the flag is meant to move. */
  recoveredAfterRetry: number
  /** Individual failed presses by classified kind (sums to ≥ `failures`). */
  pressFailuresByKind: Record<CaptureErrorKind, number>
  /**
   * Wall-clock of ONE `camera.captureImageAsFile()` call — the single libgphoto2
   * capture that contains the blocking autofocus. This is the number a prefocus
   * change is supposed to move. Recorded for every press, successful or not: a press
   * that fails on "Perhaps no focus?" burned the full AF budget before it failed, and
   * dropping those would flatter the result.
   */
  pressDurations: DurationSnapshot
  /**
   * Wall-clock of a whole capture REQUEST, retries included, for successful captures
   * only. This is what the guest actually waits through; a failed request's duration
   * is dominated by the retry schedule rather than by the camera, so it is excluded.
   */
  captureDurations: DurationSnapshot
}

function emptyByKind(): Record<CaptureErrorKind, number> {
  return { focus: 0, busy: 0, transient: 0, 'no-frame': 0, unknown: 0 }
}

interface DurationAccumulator {
  count: number
  totalMs: number
  maxMs: number
  buckets: number[]
}

function emptyDurations(): DurationAccumulator {
  return {
    count: 0,
    totalMs: 0,
    maxMs: 0,
    buckets: new Array(SHUTTER_DURATION_BUCKET_BOUNDS_MS.length + 1).fill(0)
  }
}

function addDuration(acc: DurationAccumulator, ms: number): void {
  // A non-finite or negative sample means a clock problem, not a slow camera; counting
  // it would poison totals and percentiles for the whole run.
  if (!Number.isFinite(ms) || ms < 0) return
  acc.count += 1
  acc.totalMs += ms
  if (ms > acc.maxMs) acc.maxMs = ms
  const index = SHUTTER_DURATION_BUCKET_BOUNDS_MS.findIndex((bound) => ms <= bound)
  acc.buckets[index === -1 ? SHUTTER_DURATION_BUCKET_BOUNDS_MS.length : index] += 1
}

/**
 * Upper bound of the bucket holding the `fraction` percentile, or null when it lands
 * in the overflow bucket. Uses the same "first bucket whose cumulative count reaches
 * the target" rule as a Prometheus histogram_quantile with no interpolation.
 */
function percentile(acc: DurationAccumulator, fraction: number): number | null {
  if (acc.count === 0) return null
  const target = fraction * acc.count
  let cumulative = 0
  for (let i = 0; i < acc.buckets.length; i++) {
    cumulative += acc.buckets[i]
    if (cumulative >= target) {
      return i < SHUTTER_DURATION_BUCKET_BOUNDS_MS.length
        ? SHUTTER_DURATION_BUCKET_BOUNDS_MS[i]
        : null
    }
  }
  return null
}

function snapshotDurations(acc: DurationAccumulator): DurationSnapshot {
  return {
    count: acc.count,
    totalMs: Math.round(acc.totalMs),
    maxMs: Math.round(acc.maxMs),
    meanMs: acc.count === 0 ? 0 : Math.round(acc.totalMs / acc.count),
    p50Ms: percentile(acc, 0.5),
    p95Ms: percentile(acc, 0.95),
    buckets: [...acc.buckets],
    bucketBoundsMs: SHUTTER_DURATION_BUCKET_BOUNDS_MS
  }
}

let attempts = 0
let successes = 0
let failures = 0
let recoveredAfterRetry = 0
let pressFailuresByKind = emptyByKind()
let pressDurations = emptyDurations()
let captureDurations = emptyDurations()

/** One shutter capture requested (called once per capture, before the first press). */
export function recordShutterAttempt(): void {
  attempts += 1
}

/**
 * One individual press failed and was classified. Called for every failed press,
 * including ones that a later retry recovers from — that is what makes the busy/focus
 * split readable independently of the final outcome.
 */
export function recordShutterPressFailure(kind: CaptureErrorKind): void {
  pressFailuresByKind[kind] = (pressFailuresByKind[kind] ?? 0) + 1
}

/**
 * How long ONE press took, successful or not. Called from the single site that awaits
 * `camera.captureImageAsFile()`, so it measures the libgphoto2 capture and nothing
 * around it (no wake, no event drain, no retry backoff).
 */
export function recordShutterPressDuration(ms: number): void {
  addDuration(pressDurations, ms)
}

/** How long a whole successful capture request took, retries and backoff included. */
export function recordShutterCaptureDuration(ms: number): void {
  addDuration(captureDurations, ms)
}

/** The capture produced a file. `retries` is how many re-presses it took (0 = first try). */
export function recordShutterSuccess(retries: number): void {
  successes += 1
  if (retries > 0) recoveredAfterRetry += 1
}

/** The capture exhausted its retry schedule and gave up. */
export function recordShutterFailure(): void {
  failures += 1
}

export function getCaptureStatsSnapshot(): CaptureStatsSnapshot {
  return {
    attempts,
    successes,
    failures,
    recoveredAfterRetry,
    pressFailuresByKind: { ...pressFailuresByKind },
    pressDurations: snapshotDurations(pressDurations),
    captureDurations: snapshotDurations(captureDurations)
  }
}

/** Test-only reset; production never clears counters mid-run. */
export function __resetCaptureStatsForTest(): void {
  attempts = 0
  successes = 0
  failures = 0
  recoveredAfterRetry = 0
  pressFailuresByKind = emptyByKind()
  pressDurations = emptyDurations()
  captureDurations = emptyDurations()
}
